import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';

// src/components/pitch-gauge.tsx
var mapCentsToPercent = (cents) => {
  const clampedCents = Math.max(-50, Math.min(50, cents));
  return (clampedCents + 50) / 100;
};
var smoothValue = (current, target, alpha = 0.3) => {
  return current + (target - current) * alpha;
};
var FLAT_COLOR = "#ef4444";
var INTUNE_COLOR = "#10b981";
var SHARP_COLOR = "#f59e0b";
var polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = angleDeg * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
};
var describeArcBand = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const outerStart = polarToCartesian(cx, cy, rOuter, startAngle);
  const outerEnd = polarToCartesian(cx, cy, rOuter, endAngle);
  const innerStart = polarToCartesian(cx, cy, rInner, endAngle);
  const innerEnd = polarToCartesian(cx, cy, rInner, startAngle);
  const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${innerEnd.x} ${innerEnd.y}`,
    "Z"
  ].join(" ");
};
var GaugeSVG = React.memo(({ percent }) => {
  const cx = 100, cy = 100, rOuter = 85, rInner = 68;
  const flatBand = describeArcBand(cx, cy, rOuter, rInner, 121, 179);
  const inTuneBand = describeArcBand(cx, cy, rOuter, rInner, 61, 119);
  const sharpBand = describeArcBand(cx, cy, rOuter, rInner, 1, 59);
  const trackBand = describeArcBand(cx, cy, rOuter + 1, rInner - 1, 0, 180);
  const needleAngle = 180 - percent * 180;
  const needleLen = rInner - 4;
  const needleTip = polarToCartesian(cx, cy, needleLen, needleAngle);
  const needleBase1 = polarToCartesian(cx, cy, 5, needleAngle + 90);
  const needleBase2 = polarToCartesian(cx, cy, 5, needleAngle - 90);
  return /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 200 115", className: "w-full h-full", children: [
    /* @__PURE__ */ jsx("path", { d: trackBand, fill: "var(--gauge-track, #e5e7eb)", opacity: 0.3 }),
    /* @__PURE__ */ jsx("path", { d: flatBand, fill: FLAT_COLOR, opacity: 0.75 }),
    /* @__PURE__ */ jsx("path", { d: inTuneBand, fill: INTUNE_COLOR, opacity: 0.75 }),
    /* @__PURE__ */ jsx("path", { d: sharpBand, fill: SHARP_COLOR, opacity: 0.75 }),
    (() => {
      const inner = polarToCartesian(cx, cy, rInner - 5, 90);
      const outer = polarToCartesian(cx, cy, rOuter + 5, 90);
      return /* @__PURE__ */ jsx(
        "line",
        {
          x1: inner.x,
          y1: inner.y,
          x2: outer.x,
          y2: outer.y,
          stroke: "var(--gauge-tick, #4b5563)",
          strokeWidth: 2.5
        }
      );
    })(),
    [45, 135].map((angle) => {
      const inner = polarToCartesian(cx, cy, rInner - 3, angle);
      const outer = polarToCartesian(cx, cy, rOuter + 3, angle);
      return /* @__PURE__ */ jsx(
        "line",
        {
          x1: inner.x,
          y1: inner.y,
          x2: outer.x,
          y2: outer.y,
          stroke: "var(--gauge-tick-minor, #6b7280)",
          strokeWidth: 1.5
        },
        angle
      );
    }),
    /* @__PURE__ */ jsx("text", { x: "22", y: cy + 14, fontSize: "10", fill: "var(--gauge-label, #4b5563)", fontWeight: "500", textAnchor: "middle", children: "\u266D" }),
    /* @__PURE__ */ jsx("text", { x: "178", y: cy + 14, fontSize: "10", fill: "var(--gauge-label, #4b5563)", fontWeight: "500", textAnchor: "middle", children: "\u266F" }),
    /* @__PURE__ */ jsx(
      "polygon",
      {
        points: `${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`,
        fill: "var(--gauge-needle, #374151)"
      }
    ),
    /* @__PURE__ */ jsx("circle", { cx, cy, r: 6, fill: "var(--gauge-needle, #374151)" }),
    /* @__PURE__ */ jsx("circle", { cx, cy, r: 3, fill: "var(--gauge-tick-minor, #6b7280)" })
  ] });
});
var PitchGaugeComponent = ({
  note,
  cents,
  className = "",
  clarity,
  inTuneThreshold = 5,
  smoothingFactor = 0.7,
  subdueNote = false,
  size = "md"
}) => {
  const compact = size === "sm";
  const [smoothedCents, setSmoothedCents] = useState(cents);
  const animationFrameRef = useRef(null);
  useEffect(() => {
    let isAnimating = true;
    const updateSmoothedValue = () => {
      if (!isAnimating) return;
      setSmoothedCents((prevSmoothed) => {
        const baseAlpha = 0.05 + (1 - smoothingFactor) * 0.75;
        const diff = Math.abs(cents - prevSmoothed);
        const adaptiveFactor = diff > 10 ? 1.5 : diff > 5 ? 1 : 0.6;
        const alpha = Math.min(1, baseAlpha * adaptiveFactor);
        const newValue = smoothValue(prevSmoothed, cents, alpha);
        if (Math.abs(newValue - cents) < 0.5) {
          return cents;
        }
        return newValue;
      });
      if (isAnimating) {
        animationFrameRef.current = requestAnimationFrame(updateSmoothedValue);
      }
    };
    animationFrameRef.current = requestAnimationFrame(updateSmoothedValue);
    return () => {
      isAnimating = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [cents, smoothingFactor]);
  const percent = useMemo(() => mapCentsToPercent(smoothedCents), [smoothedCents]);
  const displayCents = useMemo(() => Math.round(smoothedCents), [smoothedCents]);
  const direction = useMemo(
    () => displayCents < -inTuneThreshold ? "\u266D" : displayCents > inTuneThreshold ? "\u266F" : "",
    [displayCents, inTuneThreshold]
  );
  const inTune = Math.abs(displayCents) <= inTuneThreshold;
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col items-center ${className}`, children: [
    /* @__PURE__ */ jsx("span", { className: `font-bold drop-shadow-md transition-all duration-200 mb-1 ${subdueNote ? compact ? "text-lg opacity-60" : "text-2xl opacity-60" : compact ? "text-2xl" : "text-4xl"} ${inTune ? "text-green-600 dark:text-green-400" : "text-gray-800 dark:text-gray-200"}`, children: note }),
    /* @__PURE__ */ jsx("div", { className: compact ? "w-36" : "w-56", children: /* @__PURE__ */ jsx(GaugeSVG, { percent }) }),
    /* @__PURE__ */ jsxs("div", { className: "text-center mt-1", children: [
      /* @__PURE__ */ jsxs("span", { className: `${compact ? "text-lg" : "text-2xl"} font-mono font-semibold transition-colors duration-200 ${Math.abs(displayCents) <= inTuneThreshold ? "text-green-600 dark:text-green-400" : displayCents < 0 ? "text-red-500 dark:text-red-400" : "text-amber-500 dark:text-amber-400"}`, children: [
        direction && /* @__PURE__ */ jsx("span", { className: "mr-1", children: direction }),
        Math.abs(displayCents),
        "\xA2"
      ] }),
      /* @__PURE__ */ jsx("div", { className: `${compact ? "text-xs" : "text-sm"} text-muted-foreground mt-0.5`, children: inTune ? "In tune! \u2713" : displayCents < 0 ? "Too flat" : "Too sharp" }),
      clarity !== void 0 && /* @__PURE__ */ jsxs("div", { className: "text-xs font-medium text-gray-600 dark:text-gray-300 mt-0.5", children: [
        "clarity: ",
        Math.round(clarity * 100),
        "%"
      ] })
    ] })
  ] });
};
var PitchGauge = React.memo(PitchGaugeComponent, (prevProps, nextProps) => {
  return prevProps.note === nextProps.note && Math.abs(prevProps.cents - nextProps.cents) < 0.5 && prevProps.clarity === nextProps.clarity && prevProps.className === nextProps.className && prevProps.inTuneThreshold === nextProps.inTuneThreshold && prevProps.smoothingFactor === nextProps.smoothingFactor && prevProps.subdueNote === nextProps.subdueNote && prevProps.size === nextProps.size;
});
function useReferenceTonePlayer() {
  const [playingKey, setPlayingKey] = useState(null);
  const [playingString, setPlayingString] = useState(null);
  const [sequenceKey, setSequenceKey] = useState(null);
  const toneContextRef = useRef(null);
  const toneOscRef = useRef(null);
  const toneGainRef = useRef(null);
  const toneTimeoutRef = useRef(null);
  const sequenceTimeoutRef = useRef(null);
  const playbackTokenRef = useRef(0);
  const sequenceKeyRef = useRef(null);
  useEffect(() => {
    sequenceKeyRef.current = sequenceKey;
  }, [sequenceKey]);
  const clearToneTimeout = useCallback(() => {
    if (toneTimeoutRef.current) {
      clearTimeout(toneTimeoutRef.current);
      toneTimeoutRef.current = null;
    }
  }, []);
  const clearSequenceTimeout = useCallback(() => {
    if (sequenceTimeoutRef.current) {
      clearTimeout(sequenceTimeoutRef.current);
      sequenceTimeoutRef.current = null;
    }
  }, []);
  const releaseTone = useCallback(() => {
    const gain = toneGainRef.current;
    const osc = toneOscRef.current;
    const ctx = toneContextRef.current;
    if (!gain || !osc || !ctx) return;
    toneGainRef.current = null;
    toneOscRef.current = null;
    try {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.15);
      osc.stop(now + 0.2);
    } catch {
    }
  }, []);
  const ensureAudioContext = useCallback(async () => {
    if (!toneContextRef.current || toneContextRef.current.state === "closed") {
      toneContextRef.current = new AudioContext();
    }
    const ctx = toneContextRef.current;
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    return ctx;
  }, []);
  const stopTone = useCallback(() => {
    clearToneTimeout();
    setPlayingKey(null);
    setPlayingString(null);
    releaseTone();
  }, [clearToneTimeout, releaseTone]);
  const stopSequence = useCallback(() => {
    playbackTokenRef.current += 1;
    clearSequenceTimeout();
    setSequenceKey(null);
    stopTone();
  }, [clearSequenceTimeout, stopTone]);
  const playTone = useCallback(async (frequency, options = {}) => {
    clearToneTimeout();
    releaseTone();
    const ctx = await ensureAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    setPlayingKey(options.key ?? null);
    setPlayingString(options.stringNumber ?? null);
    osc.type = "sine";
    osc.frequency.value = frequency;
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.28, now + 0.05);
    osc.start(now);
    toneOscRef.current = osc;
    toneGainRef.current = gain;
    toneTimeoutRef.current = setTimeout(() => {
      setPlayingKey(null);
      setPlayingString(null);
      releaseTone();
      toneTimeoutRef.current = null;
    }, options.durationMs ?? 5e3);
  }, [clearToneTimeout, ensureAudioContext, releaseTone]);
  const startTone = useCallback(async (frequency, options = {}) => {
    playbackTokenRef.current += 1;
    clearSequenceTimeout();
    setSequenceKey(null);
    await playTone(frequency, options);
  }, [clearSequenceTimeout, playTone]);
  const playSequence = useCallback((notes, options) => {
    if (sequenceKeyRef.current === options.key) {
      stopSequence();
      return;
    }
    playbackTokenRef.current += 1;
    const token = playbackTokenRef.current;
    const orderedNotes = [...notes].sort((a, b) => a.frequency - b.frequency);
    const noteDurationMs = options.noteDurationMs ?? 420;
    const gapMs = options.gapMs ?? 100;
    clearSequenceTimeout();
    setSequenceKey(options.key);
    const playNext = async (index) => {
      if (token !== playbackTokenRef.current) return;
      if (index >= orderedNotes.length) {
        setSequenceKey(null);
        stopTone();
        return;
      }
      const note = orderedNotes[index];
      await playTone(note.frequency, {
        durationMs: noteDurationMs,
        key: note.key,
        stringNumber: note.string
      });
      if (token !== playbackTokenRef.current) return;
      sequenceTimeoutRef.current = setTimeout(() => {
        void playNext(index + 1);
      }, noteDurationMs + gapMs);
    };
    void playNext(0);
  }, [clearSequenceTimeout, playTone, stopSequence, stopTone]);
  useEffect(() => {
    const handleGlobalRelease = () => {
      if (!sequenceKeyRef.current && toneOscRef.current) {
        stopTone();
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopSequence();
      }
    };
    window.addEventListener("pointerup", handleGlobalRelease);
    window.addEventListener("pointercancel", handleGlobalRelease);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pointerup", handleGlobalRelease);
      window.removeEventListener("pointercancel", handleGlobalRelease);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [stopSequence, stopTone]);
  useEffect(() => {
    return () => {
      clearToneTimeout();
      clearSequenceTimeout();
      releaseTone();
      void toneContextRef.current?.close();
      toneContextRef.current = null;
    };
  }, [clearSequenceTimeout, clearToneTimeout, releaseTone]);
  return {
    playSequence,
    playingKey,
    playingString,
    sequenceKey,
    startTone,
    stopSequence,
    stopTone
  };
}

export { PitchGauge, useReferenceTonePlayer };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map